/**
 * Header trang chủ — sticky, thu gọn khi scroll.
 *
 * Gồm logo, tìm kiếm, yêu thích, menu user và HomeCategoryBar.
 * Yêu thích khi chưa login mở dialog đăng nhập trước.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { categoryApi } from '../../features/categories/api/categoryApi';
import { HeaderUserMenuPanel } from '../Common/HeaderUserMenuPanel';
import { useLoginDialog } from '../../hooks/useLoginDialog';
import { useRegisterDialog } from '../../hooks/useRegisterDialog';
import { useHeaderUserMenu } from '../../hooks/useHeaderUserMenu';
import { APP_ROUTES } from '../../shared/constants/routes';
import { TEXT } from '../../shared/languages';
import { parsePropertySearchParams } from '../../shared/utils/propertySearchParams';
import { HomeCategoryBar } from './HomeCategoryBar';
import { HeaderSearch } from '../Common/HeaderSearch';
import '../Common/HeaderUserMenu.css';
import './HomeHeader.css';

/** Header trang chủ với category bar và tìm kiếm. */
export const HomeHeader = () => {
  const { openLoginDialog } = useLoginDialog();
  const { openRegisterDialog } = useRegisterDialog();
  const navigate = useNavigate();
  const location = useLocation();
  const [categories, setCategories] = useState([]);
  const [isScrolled, setIsScrolled] = useState(false);
  const {
    user,
    isAuthenticated,
    isLoggingOut,
    isMenuOpen,
    setIsMenuOpen,
    menuRef,
    closeMenu,
    handleLogout,
    canAccessAdmin,
  } = useHeaderUserMenu();
  const nav = TEXT.nav;

  useEffect(() => {
    categoryApi
      .getCategories()
      .then((response) => setCategories(response.data.data || []))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 12);
    };

    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const searchDefaultFilters = useMemo(
    () => parsePropertySearchParams(new URLSearchParams(location.search)),
    [location.search]
  );

  return (
    <header className={`home-header ${isScrolled ? 'is-scrolled' : ''}`}>
      <div className="home-header-container">
        <Link to={APP_ROUTES.home} className="home-logo" onClick={closeMenu}>
          <svg viewBox="0 0 32 32" fill="currentColor" aria-hidden="true">
            <path d="M16 3.5c-1.2 3.8-4.8 6.4-8.7 6.4-.3 0-.6 0-.9-.1 2.8 2.1 6.3 3.4 10.1 3.4 3.8 0 7.3-1.3 10.1-3.4-.3 0-.6.1-.9.1-3.9 0-7.5-2.6-8.7-6.4z" />
            <path d="M16 28.5c1.2-3.8 4.8-6.4 8.7-6.4.3 0 .6 0 .9.1-2.8-2.1-6.3-3.4-10.1-3.4-3.8 0-7.3 1.3-10.1 3.4.3 0 .6-.1.9-.1 3.9 0 7.5 2.6 8.7 6.4z" />
          </svg>
          <span className="home-logo-text">{TEXT.common.brandName}</span>
        </Link>

        <div className="home-header-search">
          <HeaderSearch variant="wide" defaultFilters={searchDefaultFilters} />
        </div>

        <div className="home-header-actions">
          <Link
            to={isAuthenticated ? APP_ROUTES.favorites : APP_ROUTES.home}
            className="home-header-wishlist"
            aria-label={nav.favorites}
            onClick={(event) => {
              if (isAuthenticated) {
                closeMenu();
                return;
              }

              event.preventDefault();
              closeMenu();
              openLoginDialog({ onSuccess: () => navigate(APP_ROUTES.favorites) });
            }}
          >
            <svg viewBox="0 0 32 32" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="M16 28c-.2 0-.4-.1-.5-.2C7.5 21.1 2 16.2 2 10.5 2 6.4 5.4 3 9.5 3c2.4 0 4.7 1.2 6.1 3.1C17 4.2 19.3 3 21.7 3 25.8 3 29 6.4 29 10.5c0 5.7-5.5 10.6-13.5 17.3-.1.1-.3.2-.5.2z" />
            </svg>
          </Link>

          <div className={`site-user-menu ${isMenuOpen ? 'is-open' : ''}`} ref={menuRef}>
            <button
              type="button"
              className="site-user-menu-trigger"
              aria-expanded={isMenuOpen}
              aria-haspopup="true"
              onClick={() => setIsMenuOpen((open) => !open)}
            >
              <svg className="site-user-menu-icon" viewBox="0 0 32 32" fill="currentColor">
                <path d="M2 8h28v2H2V8zm0 7h28v2H2v-2zm0 7h28v2H2v-2z" />
              </svg>
              <span className="site-user-menu-avatar">
                {user?.name?.charAt(0)?.toUpperCase() || '☺'}
              </span>
            </button>

            {isMenuOpen && (
              <HeaderUserMenuPanel
                isAuthenticated={isAuthenticated}
                user={user}
                canAccessAdmin={canAccessAdmin}
                isLoggingOut={isLoggingOut}
                closeMenu={closeMenu}
                onLogout={handleLogout}
                onOpenRegister={openRegisterDialog}
                onOpenLogin={openLoginDialog}
              />
            )}
          </div>
        </div>
      </div>

      <HomeCategoryBar categories={categories} />
    </header>
  );
};
